<template>
    <Tabs :selected-team="selectedTeam" tab-key="transactions" :billing-addresses="billingAddresses">
        <DataTable
            class="mt-12"
            title="Transactions"
            searchable
            :headers="headers"
            :items="transactions"
        >
            <template #item-amount="{ data }">
                <div class="flex justify-center p-6 text-gray-500">
                    ${{ data }}
                </div>
            </template>

            <template #item-created="{ data }">
                <div class="flex justify-center p-6 text-gray-500">
                    {{ format(new Date(data), 'd. L. yyyy HH:mm:ss') }}
                </div>
            </template>

            <template #item-actions="{ row }">
                <div class="flex justify-end p-6">
                    <AtomicButton
                        size="sm"
                        color="yellow"
                        @click="
                            showInvoice(row.id as string, row.type as string)
                        "
                    >
                        <template #icon>
                            <DocumentTextIcon class="h-6 w-6" />
                        </template>
                    </AtomicButton>
                </div>
            </template>
        </DataTable>
    </Tabs>
</template>
<script lang="ts" setup>
import { DocumentTextIcon } from '@heroicons/vue/24/outline';
import IDataTableHeader from '@/components/Atomic/DataTable/types/IDataTableHeader';
import IPaginationData from '@/components/Atomic/DataTable/types/IPaginationData';
import ICurrentTeam from '@/types/Members/ICurrentTeam';
import DataTable from '@/components/Atomic/DataTable/DataTable.vue';
import { format } from 'date-fns';
import AtomicButton from '@atomic/Buttons/AtomicButton.vue';

import Tabs from '../components/TeamTabs.vue';
import useConfirm from '@composables/useConfirm';
import axios from 'axios';
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import usePdf from '@/composables/usePdf';
import IBillingAddress from '@/types/BillingAddresses/IBillingAddress';

const props = defineProps<{
    selectedTeam: ICurrentTeam;
    transactions: IPaginationData<ICurrentTeam>;
        billingAddresses: IBillingAddress[];
}>();

const { download } = usePdf();

const headers: IDataTableHeader[] = [
    {
        text: 'ID',
        id: 'id',
    },
    {
        text: 'Status',
        id: 'status',
    },
    {
        text: 'Amount',
        id: 'amount',
        align: 'center',
    },
    {
        text: 'Created at',
        id: 'created',
        align: 'center',
    },
    {
        text: '',
        id: 'actions',
        align: 'right',
        sortable: false,
    },
];

const { show: showConfirmation } = useConfirm();

const showInvoice = async (transactionId: string, type: string) => {
    const braintreeShowRoute = route(
        'kiosk.braintree-transactions.invoices.show',
        {
            transaction: transactionId,
        }
    );
    const stripeShowRoute = route('kiosk.stripe-transactions.invoices.show', {
        transaction: transactionId,
    });

    const braintreeStoreRoute = route(
        'kiosk.braintree-transactions.invoices.store',
        {
            transaction: transactionId,
        }
    );
    const stripeStoreRoute = route('kiosk.stripe-transactions.invoices.store', {
        transaction: transactionId,
    });

    const response = await axios.get(
        type === 'braintree' ? braintreeShowRoute : stripeShowRoute
    );

    if (response.data.success) {
        download(
            route('kiosk.invoices.download-pdf', {
                invoice: response.data.data.id,
            }),
            'Invoice'
        );
    } else {
        const confirmed = await showConfirmation({
            message: `Do you really want to create invoice for this transaction?`,
            type: 'danger',
        });

        if (confirmed) {
            const response = await axios.post(
                type === 'braintree' ? braintreeStoreRoute : stripeStoreRoute,
                {
                    teamId: props.selectedTeam.id,
                }
            );

            if (response.data.success) {
                download(
                    route('kiosk.invoices.download-pdf', {
                        invoice: response.data.data.id,
                    }),
                    'Invoice'
                );
            } else {
                create({
                    title: 'Unsuccessful invoice creation',
                    text: 'Invoice has not been created',
                    type: 'error',
                });
            }
        }
    }
};
</script>
